// src/components/Sidebar.jsx
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { NavLink } from 'react-router-dom';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
import { FiHome, FiList, FiCalendar, FiCompass, FiSettings } from 'react-icons/fi';
import { FaHome, FaList, FaCalendar, FaCompass, FaCog } from 'react-icons/fa';
import { getApiUrl, API_ENDPOINTS } from '../config/apiConfig';

const MOBILE_BREAKPOINT = 768;

const SidebarContainer = styled(motion.nav)`
  position: fixed; /* kesinlikle fixed */
  z-index: 1000;
  background: rgba(10, 2, 28, 0.95);
  backdrop-filter: blur(15px);
  overflow: hidden;
  margin: 0;
  padding: 0;
  box-sizing: border-box;

  ${({ $isMobile }) =>
    $isMobile
      ? `
    bottom: 0;
    left: 0;
    right: 0;
    height: 70px;
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: space-around;
    border-top: 1px solid var(--border-color);
  `
      : `
    top: 0;
    left: 0;
    width: auto;
    height: 100vh;
    display: flex;
    flex-direction: column;
  `}
`;

/* Logo (desktop only) */
const LogoContainer = styled(NavLink)`
  display: ${({ $isMobile }) => ($isMobile ? 'none' : 'flex')};
  align-items: center;
  gap: 15px;
  height: 60px;
  min-height: 60px;
  text-decoration: none;
  padding: 0 25px;
  flex-shrink: 0;
  margin-bottom: 10px;
`;

const LogoIcon = styled.div`
  font-size: 2.2rem;
  font-weight: 800;
  color: var(--primary-color);
  span {
    color: var(--sidebar-indicator-color);
  }
`;

const LogoImage = styled(motion.img)`
  height: 40px;
  width: auto;
`;

const LogoText = styled(motion.h1)`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--primary-color);
  margin: 0;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  justify-content: space-between;
  overflow: hidden;
`;

const MenuLinks = styled.div`
  display: flex;
  flex-direction: ${({ $isMobile }) => ($isMobile ? 'row' : 'column')};
  gap: ${({ $isMobile }) => ($isMobile ? '0' : '10px')};
  justify-content: ${({ $isMobile }) =>
    $isMobile ? 'space-around' : 'flex-start'};
  align-items: ${({ $isMobile }) =>
    $isMobile ? 'center' : 'stretch'};
  width: 100%;
  padding: ${({ $isMobile }) => ($isMobile ? '0' : '20px 0')};
`;

const BottomMenu = styled.div`
  display: ${({ $isMobile }) => ($isMobile ? 'none' : 'flex')};
  flex-direction: column;
  justify-content: flex-end;
  padding: 20px 0;
  width: 100%;
  flex-shrink: 0;
`;

const MenuItem = styled(NavLink)`
  display: flex;
  align-items: center;
  justify-content: ${({ $isMobile }) => ($isMobile ? 'center' : 'flex-start')};
  color: var(--secondary-color);
  text-decoration: none;
  height: 50px;
  padding: ${({ $isMobile }) => ($isMobile ? '20px 0 0 0' : '0 0 0 32px')};
  font-size: 1rem;
  font-weight: 500;
  position: relative;
  transition: color 0.2s ease, background-color 0.2s ease;
  flex: ${({ $isMobile }) => ($isMobile ? '1' : 'none')};

  &:hover {
    color: var(--primary-color);
    background-color: ${({ $isMobile }) =>
      $isMobile ? 'transparent' : 'rgba(255, 255, 255, 0.03)'};
  }

  &.active {
    color: var(--primary-color);
  }
`;

const IconWrapper = styled.div`
  font-size: ${({ $isMobile }) => ($isMobile ? '1.6rem' : '1.8rem')};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Label = styled(motion.span)`
  margin-left: ${({ $isMobile }) => ($isMobile ? '0' : '50px')};
  white-space: nowrap;
  font-size: ${({ $isMobile }) => ($isMobile ? '0.7rem' : '1rem')};
`;

const ActiveIndicator = styled(motion.div)`
  position: absolute;
  background: var(--sidebar-indicator-color);
  ${({ $isMobile }) =>
    $isMobile
      ? `
    bottom: -4px;
    left: 25%;
    right: 25%;
    height: 3px;
    border-radius: 0 0 4px 4px;
  `
      : `
    left: 0;
    top: 20%;
    bottom: 20%;
    width: 4px;
    border-radius: 0 4px 4px 0;
  `}
`;

const NavItem = ({ to, label, Icon, ActiveIcon, isEnd = false, isSidebarExpanded, isMobile }) => (
  <MenuItem to={to} end={isEnd} $isMobile={isMobile}>
    {({ isActive }) => (
      <>
        <IconWrapper $isMobile={isMobile}>
          <AnimatePresence initial={false} mode="wait">
            <motion.div
              key={isActive ? 'active' : 'inactive'}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
            >
              {isActive ? <ActiveIcon /> : <Icon />}
            </motion.div>
          </AnimatePresence>
        </IconWrapper>

        {!isMobile && isSidebarExpanded && (
          <Label
            $isMobile={isMobile}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            {label}
          </Label>
        )}

        {isActive && <ActiveIndicator $isMobile={isMobile} layoutId="active-indicator" />}
      </>
    )}
  </MenuItem>
);

const Sidebar = ({ isExpanded, setExpanded }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= MOBILE_BREAKPOINT);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch(getApiUrl(API_ENDPOINTS.GET_PUBLIC_SETTINGS));
        if (response.ok) {
          const data = await response.json();
          setSettings(data);
        }
      } catch (error) {
        console.error("Ayarlar alınırken hata:", error);
      }
    };
    fetchSettings();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= MOBILE_BREAKPOINT;
      setIsMobile(mobile);
      if (mobile) setExpanded(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [setExpanded]);

  const sidebarVariants = {
    collapsed: isMobile ? { width: '100%' } : { width: '90px' },
    expanded: isMobile ? { width: '100%' } : { width: '250px' },
  };

  return (
    <SidebarContainer
      variants={sidebarVariants}
      animate={isMobile ? 'expanded' : isExpanded ? 'expanded' : 'collapsed'}
      transition={{ duration: 0.35 }}
      onMouseEnter={isMobile ? undefined : () => setExpanded(true)}
      onMouseLeave={isMobile ? undefined : () => setExpanded(false)}
      $isMobile={isMobile}
    >
      {!isMobile && (
        <LogoContainer to="/" $isMobile={isMobile}>
          {settings.logo_type === 'image' && settings.logo_url ? (
            <LogoImage 
              src={settings.logo_url} 
              alt={settings.site_title || 'Logo'}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            />
          ) : (
            <LogoIcon>
              {settings.logo_initial || 'C'}<span>.</span>
            </LogoIcon>
          )}
          <AnimatePresence>
            {isExpanded && (
              <LogoText
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                {settings.logo_text || 'Cineglass'}
              </LogoText>
            )}
          </AnimatePresence>
        </LogoContainer>
      )}

      <ContentContainer>
        <LayoutGroup>
          <MenuLinks $isMobile={isMobile}>
            <NavItem 
              to="/" 
              label="Anasayfa" 
              Icon={FiHome} 
              ActiveIcon={FaHome} 
              isEnd 
              isSidebarExpanded={isExpanded} 
              isMobile={isMobile} 
            />
            <NavItem 
              to="/tum-bolumler" 
              label="Tüm Bölümler" 
              Icon={FiList} 
              ActiveIcon={FaList} 
              isSidebarExpanded={isExpanded} 
              isMobile={isMobile} 
            />
            <NavItem 
              to="/kesfet" 
              label="Keşfet" 
              Icon={FiCompass} 
              ActiveIcon={FaCompass} 
              isSidebarExpanded={isExpanded} 
              isMobile={isMobile} 
            />
            <NavItem 
              to="/takvim" 
              label="Takvim" 
              Icon={FiCalendar} 
              ActiveIcon={FaCalendar} 
              isSidebarExpanded={isExpanded} 
              isMobile={isMobile} 
            />

            {isMobile && (
              <NavItem 
                to="/ayarlar" 
                label="Ayarlar" 
                Icon={FiSettings} 
                ActiveIcon={FaCog} 
                isSidebarExpanded={false} 
                isMobile={isMobile} 
              />
            )}
          </MenuLinks>

          {!isMobile && (
            <BottomMenu $isMobile={isMobile}>
              <NavItem 
                to="/ayarlar" 
                label="Ayarlar" 
                Icon={FiSettings} 
                ActiveIcon={FaCog} 
                isSidebarExpanded={isExpanded} 
                isMobile={isMobile} 
              />
            </BottomMenu>
          )}
        </LayoutGroup>
      </ContentContainer>
    </SidebarContainer>
  );
};

export default React.memo(Sidebar);
